import { AgentRuntime } from "./core/agent-runtime";
import {
  SYNTHETIC_SLOTS,
  type AgentRequest,
  type AgentRunResult,
  type TraceEvent,
} from "./core/types";
import { MockCrm } from "./integrations/mock-crm";
import { SyntheticPlanner } from "./planner/synthetic-planner";

export type ScenarioId =
  | "safe_booking"
  | "missing_confirmation"
  | "stale_snapshot"
  | "idempotent_replay"
  | "idempotency_conflict"
  | "availability_only";

export interface ScenarioDefinition {
  id: ScenarioId;
  label: string;
  summary: string;
}

export interface ScenarioRun {
  scenario: ScenarioDefinition;
  result: AgentRunResult;
  executionCount: number;
  mutationCount: number;
}

export const SCENARIOS: readonly ScenarioDefinition[] = [
  {
    id: "safe_booking",
    label: "Safe booking",
    summary: "Explicit confirmation, fresh snapshot, one mutation.",
  },
  {
    id: "missing_confirmation",
    label: "Missing confirmation",
    summary: "The planner proposes a mutation; the guard blocks it.",
  },
  {
    id: "stale_snapshot",
    label: "Stale snapshot",
    summary: "Availability changes between the read and the guard.",
  },
  {
    id: "idempotent_replay",
    label: "Idempotent replay",
    summary: "The same confirmed request returns one durable result.",
  },
  {
    id: "idempotency_conflict",
    label: "Key conflict",
    summary: "The same key cannot authorize a different appointment.",
  },
  {
    id: "availability_only",
    label: "Read-only path",
    summary: "The agent lists synthetic availability without mutation.",
  },
] as const;

function requestFor(
  scenario: ScenarioId,
  slot: string = SYNTHETIC_SLOTS[0],
): AgentRequest {
  const shared = {
    sessionId: "synthetic-session-alpha",
    idempotencyKey: "synthetic-action-001",
  };

  switch (scenario) {
    case "missing_confirmation":
      return {
        ...shared,
        message: "I am comparing this option, but I am not confirming it.",
        requestedSlot: slot,
      };
    case "availability_only":
      return {
        ...shared,
        message: "Which synthetic slots are available?",
      };
    default:
      return {
        ...shared,
        message: "Please confirm this synthetic appointment.",
        requestedSlot: slot,
        confirmation: {
          action: "book_appointment",
          sessionId: shared.sessionId,
          slot,
          token: "synthetic-confirmation-001",
        },
      };
  }
}

function combineRuns(
  runs: readonly AgentRunResult[],
): AgentRunResult {
  const last = runs.at(-1);
  if (!last) throw new Error("At least one synthetic run is required.");

  let sequence = 0;
  const trace: TraceEvent[] = [];
  runs.forEach((run, runIndex) => {
    run.trace.forEach((event) => {
      sequence += 1;
      trace.push({
        ...event,
        sequence,
        data: {
          ...(event.data ?? {}),
          execution: runIndex + 1,
        },
      });
    });
  });

  return {
    ...last,
    trace,
  };
}

export async function runScenario(id: ScenarioId): Promise<ScenarioRun> {
  const scenario = SCENARIOS.find((candidate) => candidate.id === id);
  if (!scenario) throw new Error(`Unknown synthetic scenario: ${id}`);

  const crm = new MockCrm(SYNTHETIC_SLOTS);
  const planner = new SyntheticPlanner();
  let staleHookUsed = false;
  const runtime = new AgentRuntime(planner, crm, {
    beforeGuard:
      id === "stale_snapshot"
        ? (_call, port) => {
            if (!staleHookUsed && port instanceof MockCrm) {
              port.advanceAvailabilityVersion();
              staleHookUsed = true;
            }
          }
        : undefined,
  });

  const runs: AgentRunResult[] = [];
  if (id === "idempotent_replay") {
    const request = requestFor(id);
    runs.push(await runtime.run(request));
    runs.push(await runtime.run(request));
  } else if (id === "idempotency_conflict") {
    runs.push(await runtime.run(requestFor(id, SYNTHETIC_SLOTS[0])));
    runs.push(await runtime.run(requestFor(id, SYNTHETIC_SLOTS[1])));
  } else {
    runs.push(await runtime.run(requestFor(id)));
  }

  return {
    scenario,
    result: combineRuns(runs),
    executionCount: runs.length,
    mutationCount: crm.getMutationCount(),
  };
}
